import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Users, UserCheck, DollarSign, Smartphone, CheckCircle, XCircle, AlertCircle, Power, Server, MessageCircle, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSettings } from '../contexts/SettingsContext';
import { useWhatsApp } from '../contexts/WhatsAppContext';
import { getMockData } from '../data/mockDashboardData';

// Configurações de variáveis de ambiente
const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
const MAX_RETRIES = parseInt(process.env.REACT_APP_MAX_RETRIES || '3', 10);
const RETRY_DELAY = parseInt(process.env.REACT_APP_RETRY_DELAY || '1000', 10);
const ENABLE_MOCK_DATA_FALLBACK = process.env.REACT_APP_ENABLE_MOCK_DATA_FALLBACK === 'true';
const ENABLE_ERROR_LOGGING = process.env.REACT_APP_ENABLE_ERROR_LOGGING === 'true';

const Dashboard = () => {
  const { darkMode } = useSettings();
  const { whatsappStatus } = useWhatsApp();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [conversionRate, setConversionRate] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [operational, setOperational] = useState(null);
  const [averageTicket, setAverageTicket] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [usingMockData, setUsingMockData] = useState(false);
  const [restartingBackend, setRestartingBackend] = useState(false);
  const [restartingWhatsApp, setRestartingWhatsApp] = useState(false);
  const [restartingSystem, setRestartingSystem] = useState(false);

  /*
   * RATING_DASHBOARD_AUTO_NEW_LEAD_V1
   *
   * Guarda somente o ID do lead mais recente.
   * Não altera fluxo comercial, bot ou backend.
   */
  const ultimoLeadIdRef =
    React.useRef(null);

  const verificandoNovoLeadRef =
    React.useRef(false);

  const handleRestartBackend = useCallback(async () => {
    if (!window.confirm('ATENÇÃO: Isso vai reiniciar apenas o backend. Continuar?')) return;
    setRestartingBackend(true);
    try {
      const res = await fetch(`${API_URL}/admin/restart-backend`, { method: 'POST' });
      const data = await res.json();
      alert(data.success ? 'Reinicialização do backend iniciada!' : ('Erro: ' + (data.message || 'Falha')));
    } catch (e) {
      alert('Erro de conexão ao reiniciar backend.');
    } finally {
      setRestartingBackend(false);
    }
  }, []);

  const handleRestartWhatsApp = useCallback(async () => {
    if (!window.confirm('ATENÇÃO: Isso vai reiniciar a conexão do WhatsApp. Continuar?')) return;
    setRestartingWhatsApp(true);
    try {
      const res = await fetch(`${API_URL}/admin/restart-whatsapp`, { method: 'POST' });
      const data = await res.json();
      alert(data.success ? 'Reinicialização do WhatsApp iniciada!' : ('Erro: ' + (data.message || 'Falha')));
    } catch (e) {
      alert('Erro de conexão ao reiniciar WhatsApp.');
    } finally {
      setRestartingWhatsApp(false);
    }
  }, []);

  const handleRestartSystem = useCallback(async () => {
    if (!window.confirm('ATENÇÃO: Isso vai reiniciar o backend e frontend. Continuar?')) return;
    setRestartingSystem(true);
    try {
      const res = await fetch(`${API_URL}/system/restart`, { method: 'POST' });
      const data = await res.json();
      alert(data.success ? 'Reinicialização do sistema iniciada! Aguarde e recarregue a página.' : ('Erro: ' + (data.message || 'Falha')));
    } catch (e) {
      alert('Erro de conexão ao reiniciar sistema.');
    } finally {
      setRestartingSystem(false);
    }
  }, []);

  // Função auxiliar para retry com timeout
  const fetchWithRetry = useCallback(async (url, options = {}, retries = MAX_RETRIES) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (ENABLE_ERROR_LOGGING) {
        console.error(`Erro na requisição (${retries} tentativas restantes):`, error);
      }

      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchWithRetry(url, options, retries - 1);
      }

      throw error;
    }
  }, []);

  useEffect(() => {
    let ativo = true;

    /*
     * Consulta somente 1 registro.
     * O dashboard completo só é atualizado
     * quando realmente aparece outro lead.
     */
    const verificarNovoLead =
      async () => {
        if (
          !ativo ||
          document.hidden ||
          verificandoNovoLeadRef.current
        ) {
          return;
        }

        verificandoNovoLeadRef.current =
          true;

        try {
          const response =
            await axios.get(
              `${API_URL}/dashboard/recent-leads?limit=1`,
              {
                timeout:
                  Math.min(
                    API_TIMEOUT,
                    10000
                  )
              }
            );

          const leadMaisRecente =
            Array.isArray(response.data)
              ? response.data[0]
              : null;

          if (
            !leadMaisRecente ||
            leadMaisRecente.id == null
          ) {
            return;
          }

          const idMaisRecente =
            String(
              leadMaisRecente.id
            );

          /*
           * Se o carregamento inicial ainda não
           * definiu a referência, apenas memoriza.
           */
          if (
            ultimoLeadIdRef.current ===
            null
          ) {
            ultimoLeadIdRef.current =
              idMaisRecente;

            return;
          }

          /*
           * Mudou o ID = existe um novo lead.
           * Reutiliza exatamente o carregamento
           * oficial já existente da Dashboard.
           */
          if (
            idMaisRecente !==
            ultimoLeadIdRef.current
          ) {
            await fetchDashboardData();
          }
        } catch (error) {
          if (ENABLE_ERROR_LOGGING) {
            console.error(
              '[DASHBOARD AUTO LEAD] Erro ao verificar novo lead:',
              error
            );
          }
        } finally {
          verificandoNovoLeadRef.current =
            false;
        }
      };

    /*
     * Mantém o carregamento normal que
     * a Dashboard já fazia ao abrir.
     */
    fetchDashboardData();

    /*
     * Verificação leve a cada 5 segundos.
     */
    const intervalId =
      window.setInterval(
        verificarNovoLead,
        5000
      );

    /*
     * Se o usuário voltar para a aba,
     * verifica imediatamente.
     */
    const aoVoltarParaAba = () => {
      if (!document.hidden) {
        verificarNovoLead();
      }
    };

    document.addEventListener(
      'visibilitychange',
      aoVoltarParaAba
    );

    return () => {
      ativo = false;

      window.clearInterval(
        intervalId
      );

      document.removeEventListener(
        'visibilitychange',
        aoVoltarParaAba
      );

      verificandoNovoLeadRef.current =
        false;
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Configurar axios com timeout
      const axiosConfig = {
        timeout: API_TIMEOUT
      };

      const [
        statsRes,
        leadsRes,
        conversionRes,
        revenueRes,
        operationalRes,
        averageTicketRes,
        funnelRes
      ] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`, axiosConfig),
        axios.get(`${API_URL}/dashboard/recent-leads?limit=5`, axiosConfig),
        axios.get(`${API_URL}/dashboard/conversion-rate`, axiosConfig),
        axios.get(`${API_URL}/dashboard/revenue?period=30`, axiosConfig),
        axios.get(`${API_URL}/dashboard/operational`, axiosConfig),
        axios.get(`${API_URL}/dashboard/average-ticket`, axiosConfig),
        axios.get(`${API_URL}/dashboard/funnel`, axiosConfig)
      ]);

      // Validar dados antes de definir estado
      setStats(statsRes.data || null);
      const leadsRecentes =
      Array.isArray(leadsRes.data)
        ? leadsRes.data
        : [];

    setRecentLeads(
      leadsRecentes
    );

    if (
      leadsRecentes.length > 0 &&
      leadsRecentes[0]?.id != null
    ) {
      ultimoLeadIdRef.current =
        String(
          leadsRecentes[0].id
        );
    }
      setConversionRate(conversionRes.data || null);
      setRevenue(revenueRes.data || null);
      setOperational(operationalRes.data || null);
      setAverageTicket(averageTicketRes.data || null);
      setFunnel(funnelRes.data || null);
      setApiError(false);
      setUsingMockData(false);
    } catch (error) {
      if (ENABLE_ERROR_LOGGING) {
        console.error('Erro ao buscar dados do dashboard:', error);
      }

      // Verificar se deve usar dados mock como fallback
      if (ENABLE_MOCK_DATA_FALLBACK) {
        console.log('Usando dados mock como fallback');
        const mockData = getMockData();
        setStats(mockData.stats);
        setRecentLeads(mockData.recentLeads);
        setConversionRate(mockData.conversionRate);
        setRevenue(mockData.revenue);
        setApiError(true);
        setUsingMockData(true);
      } else {
        setApiError(true);
        setUsingMockData(false);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-500">Carregando dashboard...</div>
        </div>
      </div>
    );
  }

  // Validar e formatar dados dos cards
  const formatarDinheiro = valor =>
    Number(valor || 0).toLocaleString(
      'pt-BR',
      {
        style: 'currency',
        currency: 'BRL'
      }
    );

  const statCards = [
    {
      title: 'Total de Leads',
      value: stats?.total_leads ?? 0,
      footer: `Hoje: ${stats?.leads_hoje ?? 0}`,
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: 'Conversas Ativas',
      value: stats?.conversas_ativas ?? 0,
      footer: `Nesta semana: ${stats?.leads_semana ?? 0}`,
      icon: MessageCircle,
      color: 'bg-indigo-500'
    },
    {
      title: 'Contratos Assinados',
      value: stats?.contratos_assinados ?? 0,
      footer: `Pagamentos aprovados: ${stats?.pagos ?? 0}`,
      icon: UserCheck,
      color: 'bg-green-500'
    },
    {
      title: 'Receita do Mês',
      value: formatarDinheiro(
        stats?.receita_mes
      ),
      footer: `Total recebido: ${formatarDinheiro(
        stats?.receita_total
      )}`,
      icon: DollarSign,
      color: 'bg-yellow-500'
    }
  ];

  const operationalCards = [
    {
      title: 'Documentação Pendente',
      value: operational?.documentacao_pendente ?? 0,
      detail: 'Clientes aguardando documentos'
    },
    {
      title: 'Em Produção',
      value: operational?.servicos_em_producao ?? 0,
      detail: 'Serviços em execução'
    },
    {
      title: 'Finalizados',
      value: operational?.servicos_entregues ?? 0,
      detail: 'Serviços concluídos'
    },
    {
      title: 'PIX Facilitado',
      value: stats?.pix_facilitado_ativos ?? 0,
      detail: formatarDinheiro(
        stats?.pix_valor_em_aberto
      )
    },
    {
      title: 'Ticket Médio',
      value: formatarDinheiro(
        averageTicket?.ticket_medio_geral
      ),
      detail: `${averageTicket?.total_vendas ?? 0} venda(s)`
    },
    {
      title: 'Conversão',
      value: `${Number(
        conversionRate?.taxa_conversao || 0
      ).toFixed(2)}%`,
      detail: `${conversionRate?.convertidos ?? 0} convertido(s)`
    }
  ];

  const funnelSteps = [
    {
      label: 'Leads',
      value: funnel?.etapa_1_entrada ?? 0
    },
    {
      label: 'Diagnóstico',
      value: funnel?.etapa_2_diagnostico ?? 0
    },
    {
      label: 'Qualificação',
      value: funnel?.etapa_3_qualificacao ?? 0
    },
    {
      label: 'Oferta',
      value: funnel?.etapa_4_oferta ?? 0
    },
    {
      label: 'Contrato',
      value: funnel?.etapa_5_contrato ?? 0
    },
    {
      label: 'Pagamento',
      value: funnel?.etapa_6_pagamento ?? 0
    },
    {
      label: 'Documentação',
      value: funnel?.etapa_7_documentacao ?? 0
    },
    {
      label: 'Produção',
      value: funnel?.etapa_8_producao ?? 0
    },
    {
      label: 'Finalizados',
      value: funnel?.etapa_9_concluido ?? 0
    }
  ];

  return (
    <div className="space-y-6">
      {/* API Error Warning */}
      {apiError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          <span className="font-medium text-yellow-800 dark:text-yellow-300">
            {usingMockData ? 'Usando dados de demonstração (API offline)' : 'Erro ao carregar dados da API'}
          </span>
        </div>
      )}

      {/* WhatsApp Status Banner */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
        whatsappStatus?.status === 'CONNECTED'
          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
      }`}>
        <Smartphone className={`w-5 h-5 ${
          whatsappStatus?.status === 'CONNECTED'
            ? 'text-green-600 dark:text-green-400' 
            : 'text-red-600 dark:text-red-400'
        }`} />
        {whatsappStatus?.status === 'CONNECTED' ? (
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
        ) : (
          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
        )}
        <span className={`font-medium ${
          whatsappStatus?.status === 'CONNECTED'
            ? 'text-green-800 dark:text-green-300' 
            : 'text-red-800 dark:text-red-300'
        }`}>
          {whatsappStatus?.status === 'CONNECTED' ? '✅ WhatsApp conectado' :
           whatsappStatus?.status === 'CONNECTING' ? '🔄 Conectando...' :
           whatsappStatus?.status === 'BANNED' ? '🚫 Conta banida' :
           '❌ WhatsApp desconectado'}
        </span>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dashboard</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Visão geral do seu negócio</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleRestartBackend}
            disabled={restartingBackend}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              restartingBackend ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-orange-600 hover:bg-orange-700 text-white'
            }`}
          >
            {restartingBackend ? <RefreshCw className="animate-spin" size={16} /> : <Server size={16} />}
            {restartingBackend ? 'Reiniciando...' : 'Reiniciar Backend'}
          </button>
          <button
            onClick={handleRestartWhatsApp}
            disabled={restartingWhatsApp}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              restartingWhatsApp ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {restartingWhatsApp ? <RefreshCw className="animate-spin" size={16} /> : <MessageCircle size={16} />}
            {restartingWhatsApp ? 'Reiniciando...' : 'Reiniciar WhatsApp'}
          </button>
          <button
            onClick={handleRestartSystem}
            disabled={restartingSystem}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              restartingSystem ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {restartingSystem ? <RefreshCw className="animate-spin" size={16} /> : <Power size={16} />}
            {restartingSystem ? 'Reiniciando...' : 'Reiniciar Sistema'}
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>{stat.title}</p>
                  <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
              <div className={
                `${
                  darkMode
                    ? 'text-gray-400'
                    : 'text-gray-500'
                } mt-4 text-sm`
              }>
                {stat.footer}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <h2 className={
          `${
            darkMode
              ? 'text-white'
              : 'text-gray-900'
          } text-lg font-semibold mb-3`
        }>
          Operação
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {operationalCards.map((card, index) => (
            <div
              key={index}
              className={
                `${
                  darkMode
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-gray-200'
                } border rounded-xl p-5 shadow-sm`
              }
            >
              <p className={
                darkMode
                  ? 'text-gray-400 text-sm'
                  : 'text-gray-500 text-sm'
              }>
                {card.title}
              </p>

              <p className={
                `${
                  darkMode
                    ? 'text-white'
                    : 'text-gray-900'
                } text-2xl font-bold mt-1`
              }>
                {card.value}
              </p>

              <p className={
                darkMode
                  ? 'text-gray-500 text-xs mt-2'
                  : 'text-gray-400 text-xs mt-2'
              }>
                {card.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={
          `${
            darkMode ? 'bg-gray-800' : 'bg-white'
          } rounded-xl shadow-sm p-6`
        }>
          <h2 className={
            `${
              darkMode ? 'text-white' : 'text-gray-900'
            } text-lg font-semibold mb-4`
          }>
            Receita dos Últimos 30 Dias
          </h2>

          {revenue?.por_dia?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={revenue.por_dia}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="data" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="receita" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Sem receita registrada no período.
            </div>
          )}
        </div>

        <div className={
          `${
            darkMode ? 'bg-gray-800' : 'bg-white'
          } rounded-xl shadow-sm p-6`
        }>
          <h2 className={
            `${
              darkMode ? 'text-white' : 'text-gray-900'
            } text-lg font-semibold mb-4`
          }>
            Taxa de Conversão
          </h2>

          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600">
                {Number(
                  conversionRate?.taxa_conversao || 0
                ).toFixed(2)}%
              </div>

              <div className={
                darkMode
                  ? 'text-gray-400 mt-3'
                  : 'text-gray-500 mt-3'
              }>
                {conversionRate?.convertidos ?? 0} de{' '}
                {conversionRate?.total ?? 0} leads
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={
        `${
          darkMode
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } border rounded-xl shadow-sm p-6`
      }>
        <h2 className={
          `${
            darkMode ? 'text-white' : 'text-gray-900'
          } text-lg font-semibold mb-5`
        }>
          Funil Comercial
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-9 gap-3">
          {funnelSteps.map((step, index) => (
            <div
              key={step.label}
              className={
                `${
                  darkMode
                    ? 'bg-gray-900 border-gray-700'
                    : 'bg-gray-50 border-gray-200'
                } border rounded-lg px-3 py-5 text-center min-h-[132px] flex flex-col items-center justify-center`
              }
            >
              <div className="text-3xl font-bold text-blue-600 leading-none">
                {step.value}
              </div>

              <div className={
                `${
                  darkMode
                    ? 'text-gray-300'
                    : 'text-gray-700'
                } text-[12px] font-medium mt-5 whitespace-nowrap`
              }>
                {index + 1}. {step.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Leads */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Leads Recentes</h2>
        {recentLeads && Array.isArray(recentLeads) && recentLeads.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-sm border-b ${darkMode ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-200'}`}>
                  <th className="pb-3">Nome</th>
                  <th className="pb-3">CPF/CNPJ</th>
                  <th className="pb-3">Telefone</th>
                  <th className="pb-3">Etapa atual</th>
                  <th className="pb-3">Origem</th>
                  <th className="pb-3">Entrada</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.map((lead) => {
                  const dataEntrada = lead.data_criacao
                    ? new Date(lead.data_criacao)
                    : null;

                  const etapa = String(
                    lead.stage ||
                    lead.status ||
                    'NÃO INFORMADA'
                  );

                  const etapaFormatada = etapa
                    .replace(/_/g, ' ');

                  const finalizado = [
                    'FINALIZADOS',
                    'FINALIZADO',
                    'CONCLUIDO'
                  ].includes(etapa);

                  const emAtendimento = [
                    'ABERTURA',
                    'NOVO_LEAD',
                    'EM_DIAGNOSTICO',
                    'EMPATIA_ENVIADA',
                    'EDUCACAO_ATIVA',
                    'AUTORIDADE_APRESENTADA',
                    'EM_QUALIFICACAO',
                    'QUALIFICADO_PF',
                    'QUALIFICADO_PJ',
                    'QUALIFICADO_DUPLO',
                    'OFERTA_PF',
                    'OFERTA_PJ',
                    'AGUARDANDO_FORMA_PAGAMENTO',
                    'OBJECAO_ATIVA'
                  ].includes(etapa);

                  return (
                    <tr
                      key={lead.id}
                      className={
                        `${
                          darkMode
                            ? 'border-gray-700 hover:bg-gray-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        } border-b last:border-0`
                      }
                    >
                      <td className={
                        `${
                          darkMode
                            ? 'text-white'
                            : 'text-gray-900'
                        } py-3 font-medium`
                      }>
                        {lead.nome || 'Lead sem nome'}
                      </td>

                      <td className={
                        darkMode
                          ? 'py-3 text-gray-400'
                          : 'py-3 text-gray-600'
                      }>
                        {lead.cpf_cnpj || '—'}
                      </td>

                      <td className={
                        darkMode
                          ? 'py-3 text-gray-400'
                          : 'py-3 text-gray-600'
                      }>
                        {lead.telefone || lead.phone || '—'}
                      </td>

                      <td className="py-3">
                        <span className={
                          `${
                            finalizado
                              ? 'bg-green-100 text-green-800'
                              : emAtendimento
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                          } px-2 py-1 rounded-full text-xs font-medium`
                        }>
                          {etapaFormatada}
                        </span>
                      </td>

                      <td className={
                        darkMode
                          ? 'py-3 text-gray-400'
                          : 'py-3 text-gray-600'
                      }>
                        {lead.source || 'Não informada'}
                      </td>

                      <td className={
                        darkMode
                          ? 'py-3 text-gray-400'
                          : 'py-3 text-gray-600'
                      }>
                        {dataEntrada
                          ? dataEntrada.toLocaleDateString('pt-BR')
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 mx-auto mb-2" />
              <p>Nenhum lead encontrado</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
